import { IPoint2d } from '@/editor/math/IPoint2d';
import { Line2d } from '@/editor/math/Line2d';
import { EEntityType } from '@/editor/types';
import { EAbsorbPointType, EMeasureSubLineType, IAbsorbPointInfo, ISublineInfo } from '@/editor/types/absorb';
import { IShape2d, IShapeSeg2d } from '@/editor/types/shape';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { genPointHash, genShapeHash } from '@/editor/utils/hash';
import { getFootRatio, getLength, getPointFromRatio, pointOnSeg } from '@/editor/math/shape/other';
import {
  getDisplayAbsorbPointTypePriority,
  getPreferredDisplayAbsorbPointType,
  getTangentFootPoint
} from '../wall/utils';
import { thresholdValueMagnitude } from '@/editor/math/const';
import { RTree } from '@/editor/utils/RTree';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { globalStores } from '@/editor/stores';
import {
  filterVisibleInfos,
  getAbsorbFilterBounds,
  getAbsorbViewportBounds,
  TAbsorbViewportBounds,
  TAbsorbViewportBoundsProvider
} from '../viewportFilter';
import type { BoundingBox } from '@/editor/utils/dxf/types';

export interface IAbsorbMeasureLine {
  id: string;
  start: IPoint2d;
  end: IPoint2d;
  seg: IShapeSeg2d;
  shape: IShape2d;
  center?: IPoint2d;
  entityType?: EEntityType;
  clockwise?: boolean;
  effectiveOnEndpoint?: boolean; // 是否端点才生效
  /** 视口过滤用的包围盒，缺省时按 seg 现算，见 IViewportFilterable.bounds */
  bounds?: BoundingBox;
}

export interface IMeasureCalculateOptions {
  forceOrthogonalAbsorb?: boolean;
  extraWallInfos?: IAbsorbMeasureLine[];
  enabledLineDimension?: boolean;
}

export interface IMeasureAbsorbPointInfo extends Omit<IAbsorbPointInfo, 'sublineInfos'> {
  sublineInfos: Set<IMeasureSubLineInfo>;
}

interface IAbsorbPointRecord extends IMeasureAbsorbPointInfo {
  // 真实吸附点选择仍以交点为优先级，显示样式类型单独计算。
  hasCrossPoint: boolean;
}

interface IMeasureSubLineInfo extends Omit<ISublineInfo, 'wallId' | 'type'> {
  id: string;
  type: EMeasureSubLineType;
  entityType?: EEntityType;
}

const DEFAULT_MEASURE_ABSORB_DISTANCE = 80;
export class MeasureAbsorbCalculator {
  private _lineInfos: IAbsorbMeasureLine[] = [];
  private _extraLineInfos: IAbsorbMeasureLine[] = [];
  private _horizontalLine: Line2d | null = null;
  private _verticalLine: Line2d | null = null;
  private _normalLine: Line2d | null = null; // 面积标注下辅助法线
  private _addedLineHash = new Set<string>(); // 记录已经添加的墙线段/墙延长线，避免重复添加
  private _subLineInfos: IMeasureSubLineInfo[] = [];
  private absorbPointMap: Map<number, IAbsorbPointRecord> = new Map();
  private _pointRTree: RTree<{ pointHash: number }> = new RTree();
  private _absorbDistance: number = DEFAULT_MEASURE_ABSORB_DISTANCE;
  private _eventDispose: () => void = () => {};

  /** 当前 calculate 的视口过滤矩形，null 表示不过滤。每个 calculate 入口设置一次 */
  private _filterBounds: TAbsorbViewportBounds = null;
  private _getViewportBounds: TAbsorbViewportBoundsProvider;

  constructor(getViewportBounds: TAbsorbViewportBoundsProvider = getAbsorbViewportBounds) {
    this._getViewportBounds = getViewportBounds;
    this._eventDispose = editorEvent.on(EditorEventName.ZOOM_UPDATE_2D, this._updateAbsorbDistance);
  }

  private _updateAbsorbDistance = (sceneZoom: number) => {
    const zoom = sceneZoom / 100;
    this._absorbDistance = DEFAULT_MEASURE_ABSORB_DISTANCE / (Number.isFinite(zoom) && zoom > 0 ? zoom : 1);
  };

  private get _totalLineInfos() {
    // 只吸附视口内可见的线；extraWallInfos 是按光标邻近现取的 DXF 底图线，不参与过滤
    return [...filterVisibleInfos(this._lineInfos, this._filterBounds), ...this._extraLineInfos];
  }

  /** 参考点会并入过滤矩形，保证「参考点所在处的候选不被误杀」 */
  private _updateFilterBounds(referencePoints: IPoint2d[]) {
    this._filterBounds = getAbsorbFilterBounds(this._getViewportBounds(), referencePoints);
  }

  updateData(lineInfos: IAbsorbMeasureLine[], extraLineInfos?: IAbsorbMeasureLine[]) {
    this._lineInfos = lineInfos;
    this._extraLineInfos = extraLineInfos ?? [];
  }

  setOrthogonalData(point: IPoint2d) {
    this._horizontalLine = new Line2d({
      parametric: {
        point,
        direction: { x: 1, y: 0 }
      }
    });
    this._verticalLine = new Line2d({
      parametric: {
        point,
        direction: { x: 0, y: 1 }
      }
    });
  }

  clearOrthogonalData() {
    this._horizontalLine = null;
    this._verticalLine = null;
  }

  setNormalData(start: IPoint2d, end: IPoint2d) {
    this._normalLine = new LineSeg2d(start, end).getPerpendicularLine(Point2dUtils.getCenter(start, end)).createLine();
  }

  clearNormalData() {
    this._normalLine = null;
  }

  private _prepareOrthogonalSubLine(rawPoint: IPoint2d) {
    if (this._horizontalLine && this._verticalLine) {
      const horizontalLineFootPoint = this._horizontalLine.pointProjectOnLine(rawPoint);
      const verticalLineFootPoint = this._verticalLine.pointProjectOnLine(rawPoint);
      const toHorizontalLineDistance = Point2dUtils.distanceTo(horizontalLineFootPoint, rawPoint);
      const toVerticalLineDistance = Point2dUtils.distanceTo(verticalLineFootPoint, rawPoint);

      const addHorizontalLine = toHorizontalLineDistance < this._absorbDistance;
      const addVerticalLine = !addHorizontalLine && toVerticalLineDistance < this._absorbDistance;

      if (addHorizontalLine) {
        this._subLineInfos.push({
          id: '',
          type: EMeasureSubLineType.HorizontalLine,
          absorbPoint: horizontalLineFootPoint,
          absorbPointType: EAbsorbPointType.Foot,
          shape: this._horizontalLine
        });
        this._addedLineHash.add(genShapeHash(this._horizontalLine));
      } else if (addVerticalLine) {
        this._subLineInfos.push({
          id: '',
          type: EMeasureSubLineType.VerticalLine,
          absorbPoint: verticalLineFootPoint,
          absorbPointType: EAbsorbPointType.Foot,
          shape: this._verticalLine
        });
        this._addedLineHash.add(genShapeHash(this._verticalLine));
      }
    }
  }

  private _prepareNormalSubLine(rawPoint: IPoint2d) {
    if (this._normalLine) {
      const normalLineFootPoint = this._normalLine.pointProjectOnLine(rawPoint);
      const toNormalLineDistance = Point2dUtils.distanceTo(normalLineFootPoint, rawPoint);
      if (toNormalLineDistance < this._absorbDistance) {
        this._subLineInfos.push({
          id: '',
          type: EMeasureSubLineType.MidNormalLine,
          absorbPoint: normalLineFootPoint,
          absorbPointType: EAbsorbPointType.Foot,
          shape: this._normalLine
        });
        this._addedLineHash.add(genShapeHash(this._normalLine));
      }
    }
  }

  private _prepareLineMidPointSubLine(
    rawPoint: IPoint2d,
    lineInfo: IAbsorbMeasureLine,
    subLineInfos: IMeasureSubLineInfo[]
  ) {
    if (lineInfo.effectiveOnEndpoint) return;

    const midPoint = getPointFromRatio(lineInfo.seg, 0.5);
    if (Point2dUtils.distanceTo(midPoint, rawPoint) > this._absorbDistance) return;

    subLineInfos.push({
      id: lineInfo.id,
      type: EMeasureSubLineType.EntityBoxLine,
      absorbPoint: midPoint,
      absorbPointType: EAbsorbPointType.Center,
      shape: lineInfo.seg,
      entityType: lineInfo.entityType
    });
  }

  private _prepareEntityBoxSubLine(rawPoint: IPoint2d) {
    const lineInfos = this._totalLineInfos;
    const lineFootRatio = lineInfos.map((item) => getFootRatio(item.seg, rawPoint));
    let subLineInfos: IMeasureSubLineInfo[] = [];
    const addedLineHash = this._addedLineHash;

    // 计算墙线段/墙切线
    for (let index = 0; index < lineInfos.length; index++) {
      const lineInfo = lineInfos[index];
      if (lineInfo.entityType === EEntityType.LinearDimension) continue;

      const lineInfoSeg = lineInfo.seg;
      const footRatio = lineFootRatio[index];
      const footPoint = getTangentFootPoint(lineInfoSeg, rawPoint, footRatio);

      this._prepareLineMidPointSubLine(rawPoint, lineInfo, subLineInfos);

      if (Point2dUtils.distanceTo(footPoint, rawPoint) > this._absorbDistance) continue;

      const wallSegLength = getLength(lineInfoSeg);
      const startToFootDistance = wallSegLength * footRatio;
      const nearStart = Math.abs(startToFootDistance) < this._absorbDistance;
      const nearEnd = Math.abs(startToFootDistance - wallSegLength) < this._absorbDistance;
      if (lineInfo.effectiveOnEndpoint && !nearStart && !nearEnd) continue;

      const lineHash = genShapeHash(lineInfo.shape);
      if (nearStart || nearEnd) {
        if (nearStart) {
          // 捕获了起点
          subLineInfos.push({
            id: lineInfo.id,
            type: EMeasureSubLineType.EntityBoxLine,
            absorbPoint: lineInfoSeg.start,
            absorbPointType: EAbsorbPointType.Start,
            shape: lineInfo.seg,
            entityType: lineInfo.entityType
          });
        }

        if (nearEnd) {
          // 捕获了终点
          subLineInfos.push({
            id: lineInfo.id,
            type: EMeasureSubLineType.EntityBoxLine,
            absorbPoint: lineInfoSeg.end,
            absorbPointType: EAbsorbPointType.End,
            shape: lineInfo.seg,
            entityType: lineInfo.entityType
          });
        }
      } else if (footRatio > 0 && footRatio < 1 && pointOnSeg(lineInfoSeg, footPoint)) {
        // 捕获了线段上的垂点
        // pointOnSeg 过滤掉弧段 getTangentFootPoint 在光标离弧较远时返回的“端点切线投影点”，
        // 保证 Foot 只接受真正落在线段/弧上的垂足
        subLineInfos.push({
          id: lineInfo.id,
          type: EMeasureSubLineType.EntityBoxLine,
          absorbPoint: footPoint,
          absorbPointType: EAbsorbPointType.Foot,
          shape: lineInfo.seg,
          entityType: lineInfo.entityType
        });
      }

      addedLineHash.add(lineHash);
    }

    // 先获取端点/中点吸附的点，不参与后续相交计算
    subLineInfos = subLineInfos.filter((subLineInfo) => {
      let preserve = true;
      if (subLineInfo.absorbPointType === EAbsorbPointType.Start) {
        this.addAbsorbPoint(subLineInfo.absorbPoint, subLineInfo);
        preserve = false;
      } else if (subLineInfo.absorbPointType === EAbsorbPointType.End) {
        this.addAbsorbPoint(subLineInfo.absorbPoint, subLineInfo);
        preserve = false;
      } else if (subLineInfo.absorbPointType === EAbsorbPointType.Center) {
        this.addAbsorbPoint(subLineInfo.absorbPoint, subLineInfo);
        preserve = false;
      }
      return preserve;
    });

    this._subLineInfos.push(...subLineInfos);
  }

  private shouldUseCandidateAbsorbPoint(absorbPointInfo: IAbsorbPointRecord, candidatePointType: EAbsorbPointType) {
    if (candidatePointType === EAbsorbPointType.Cross) return true;
    if (absorbPointInfo.hasCrossPoint) return false;

    return (
      getDisplayAbsorbPointTypePriority(candidatePointType) >
      getDisplayAbsorbPointTypePriority(absorbPointInfo.absorbPointType)
    );
  }

  private addAbsorbPoint(point: IPoint2d, subLineInfo: IMeasureSubLineInfo, options?: { isCrossPoint?: boolean }) {
    let pointHash = genPointHash(point, thresholdValueMagnitude);
    const candidatePointType = options?.isCrossPoint ? EAbsorbPointType.Cross : subLineInfo.absorbPointType;

    const nearNode = this._pointRTree.searchNearNode(point);
    if (nearNode) {
      pointHash = nearNode.infos[0].pointHash;
    } else {
      this._pointRTree.addPoint(point, { pointHash });
    }

    const absorbPointInfo = this.absorbPointMap.get(pointHash);
    if (absorbPointInfo) {
      if (this.shouldUseCandidateAbsorbPoint(absorbPointInfo, candidatePointType)) {
        absorbPointInfo.absorbPoint = point;
      }
      absorbPointInfo.absorbPointType = getPreferredDisplayAbsorbPointType(
        absorbPointInfo.absorbPointType,
        candidatePointType
      );
      absorbPointInfo.hasCrossPoint = absorbPointInfo.hasCrossPoint || !!options?.isCrossPoint;
      absorbPointInfo.sublineInfos.add(subLineInfo);
    } else {
      this.absorbPointMap.set(pointHash, {
        absorbPoint: point,
        absorbPointType: candidatePointType,
        sublineInfos: new Set([subLineInfo]),
        hasCrossPoint: !!options?.isCrossPoint
      });
    }
  }

  private _subLineIntersect(rawPoint: IPoint2d) {
    // 开始计算交点
    const subLineInfos = this._subLineInfos;
    for (let i = 0; i < subLineInfos.length; i++) {
      const subLineInfo = subLineInfos[i];
      let intersected = false;

      for (let j = i + 1; j < subLineInfos.length; j++) {
        const otherSubLineInfo = subLineInfos[j];
        let _crossPoints = intersectCommonShape(subLineInfo.shape, otherSubLineInfo.shape);
        if (!_crossPoints) continue;

        _crossPoints = _crossPoints.sort((point1, point2) => {
          return Point2dUtils.distanceTo(point1, rawPoint) - Point2dUtils.distanceTo(point2, rawPoint);
        });
        if (Point2dUtils.distanceTo(_crossPoints[0], rawPoint) > this._absorbDistance) continue;

        intersected = true;
        this.addAbsorbPoint(_crossPoints[0], subLineInfo, { isCrossPoint: true });
        this.addAbsorbPoint(_crossPoints[0], otherSubLineInfo, { isCrossPoint: true });
      }

      // 如果没有交点，添加垂足
      if (!intersected) {
        this.addAbsorbPoint(subLineInfo.absorbPoint, subLineInfo);
      }
    }
  }

  private evaluatePointWeight(relativeSubLineInfos: IMeasureSubLineInfo[]) {
    let weight = 0;
    const step = 100;

    for (const subLineInfo of relativeSubLineInfos) {
      if (
        subLineInfo.absorbPointType === EAbsorbPointType.Start ||
        subLineInfo.absorbPointType === EAbsorbPointType.End
      ) {
        weight += step;
      }

      if (subLineInfo.absorbPointType === EAbsorbPointType.Center) {
        weight += step;
      }

      switch (subLineInfo.type) {
        case EMeasureSubLineType.EntityBoxLine:
        case EMeasureSubLineType.HorizontalLine:
        case EMeasureSubLineType.VerticalLine:
        case EMeasureSubLineType.MidNormalLine:
          weight++;
          break;
      }
    }

    return weight;
  }

  private _getSortedAbsorbPointList(rawPoint: IPoint2d) {
    // 添加距离参数
    const nearAbsorbPointList = Array.from(this.absorbPointMap.values());

    const absorbPointList = nearAbsorbPointList
      .map((item) => {
        const distanceToRawPoint = Point2dUtils.distanceTo(item.absorbPoint, rawPoint);
        return {
          ...item,
          absorbPriority: item.hasCrossPoint ? 1 : 0,
          weight: this.evaluatePointWeight(Array.from(item.sublineInfos)),
          distanceToRawPoint
        };
      })
      .sort((a, b) => {
        if (a.absorbPriority !== b.absorbPriority) return b.absorbPriority - a.absorbPriority;
        if (a.weight !== b.weight) return b.weight - a.weight;
        return a.distanceToRawPoint - b.distanceToRawPoint; // 升序
      });

    return absorbPointList;
  }

  calculate(rawPoint: IPoint2d, options?: IMeasureCalculateOptions): IMeasureAbsorbPointInfo | null {
    this._clear();
    this._extraLineInfos = options?.extraWallInfos ?? [];
    this._updateFilterBounds([rawPoint]);

    // 添加正交线
    this._prepareOrthogonalSubLine(rawPoint);
    // 再添加法线
    this._prepareNormalSubLine(rawPoint);
    // 再添加墙线段/墙延长线
    this._prepareEntityBoxSubLine(rawPoint);
    // 开始计算交点
    this._subLineIntersect(rawPoint);

    // ================== 对吸附点列表进行排序 ==================
    const sortedAbsorbPointList = this._getSortedAbsorbPointList(rawPoint);

    // 返回第一个吸附点
    return sortedAbsorbPointList.length > 0 ? sortedAbsorbPointList[0] : null;
  }

  calculateAbsorbByLinearDimension(startPoint: IPoint2d, endPoint: IPoint2d): IMeasureAbsorbPointInfo | null {
    this._clear();
    this._updateFilterBounds([startPoint, endPoint]);

    this._prepareLinearDimensionLine(startPoint, endPoint);
    this._subLineIntersectByLinearDimension(startPoint, endPoint);

    const neighborAbsorbPointList = Array.from(this.absorbPointMap.values());
    const absorbPointList = neighborAbsorbPointList.map((item) => {
      return {
        ...item,
        weight: this.evaluatePointWeight(Array.from(item.sublineInfos))
      };
    });

    return absorbPointList?.[0] ?? null;
  }

  private _prepareLinearDimensionLine(startPoint: IPoint2d, endPoint: IPoint2d) {
    const lineInfos = this._totalLineInfos;
    let subLineInfos: IMeasureSubLineInfo[] = [];
    const addedLineHash = this._addedLineHash;
    const currentRulerSeg = new LineSeg2d(startPoint, endPoint);
    const currentRulerLine = currentRulerSeg.createLine();
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;

    // 计算墙线段/墙切线
    for (let index = 0; index < lineInfos.length; index++) {
      const lineInfo = lineInfos[index];
      if (lineInfo.entityType !== EEntityType.LinearDimension) continue;
      if (!currentRulerSeg.isParallel(new LineSeg2d(lineInfo.start, lineInfo.end))) continue;

      const distance = currentRulerLine.pointToLineDistance(lineInfo.start);
      if (distance > dynamicAbsorbDistance) continue;

      const startFootRatio = getFootRatio(lineInfo.seg, startPoint);
      const endFootRatio = getFootRatio(lineInfo.seg, endPoint);
      const startFootPoint = getTangentFootPoint(lineInfo.seg, startPoint, startFootRatio);
      const endFootPoint = getTangentFootPoint(lineInfo.seg, endPoint, endFootRatio);

      const lineHash = genShapeHash(lineInfo.shape);
      const isAddedLine = this._addedLineHash.has(lineHash);

      if ((startFootRatio < 0 && endFootRatio < 0) || (startFootRatio > 1 && endFootRatio > 1)) {
        // 两线段不重叠，取最近的一对点
        const entityEndpoints = [lineInfo.start, lineInfo.end];
        const footPoints = [startFootPoint, endFootPoint];
        let origin = startFootPoint;
        let absorbPoint = lineInfo.start;
        let minDistance = Infinity;
        for (const footPoint of footPoints) {
          for (const entityEndpoint of entityEndpoints) {
            const distance = Point2dUtils.distanceTo(footPoint, entityEndpoint);
            if (distance < minDistance) {
              minDistance = distance;
              origin = footPoint;
              absorbPoint = entityEndpoint;
            }
          }
        }

        subLineInfos.push({
          id: lineInfo.id,
          type: EMeasureSubLineType.EntityBoxExtensionLine,
          absorbPoint,
          origin,
          absorbPointType: EAbsorbPointType.Line, // 声明为线吸附
          shape: lineInfo.shape
        });
      } else if (!isAddedLine) {
        // 部分重叠 OR 投影完全被包含 OR 投影完全包含
        subLineInfos.push({
          id: lineInfo.id,
          type: EMeasureSubLineType.EntityBoxExtensionLine,
          absorbPoint: lineInfo.end,
          origin: lineInfo.start,
          absorbPointType: EAbsorbPointType.Line, // 声明为线吸附
          shape: lineInfo.shape
        });
      }

      addedLineHash.add(lineHash);
    }

    // 先获取端点/中点吸附的点，不参与后续相交计算
    subLineInfos = subLineInfos.filter((subLineInfo) => {
      let preserve = true;
      if (subLineInfo.absorbPointType === EAbsorbPointType.Start) {
        this.addAbsorbPoint(subLineInfo.absorbPoint, subLineInfo);
        preserve = false;
      } else if (subLineInfo.absorbPointType === EAbsorbPointType.End) {
        this.addAbsorbPoint(subLineInfo.absorbPoint, subLineInfo);
        preserve = false;
      } else if (subLineInfo.absorbPointType === EAbsorbPointType.Center) {
        this.addAbsorbPoint(subLineInfo.absorbPoint, subLineInfo);
        preserve = false;
      }
      return preserve;
    });

    this._subLineInfos.push(...subLineInfos);
  }

  private _subLineIntersectByLinearDimension(startPoint: IPoint2d, endPoint: IPoint2d) {
    // 开始计算交点
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;
    const lineSeg = new LineSeg2d(startPoint, endPoint);
    const subLineInfos = this._subLineInfos;
    for (let i = 0; i < subLineInfos.length; i++) {
      const subLineInfo = subLineInfos[i];
      let intersected = false;

      for (let j = i + 1; j < subLineInfos.length; j++) {
        const otherSubLineInfo = subLineInfos[j];
        let _crossPoints = intersectCommonShape(subLineInfo.shape, otherSubLineInfo.shape);
        if (!_crossPoints) continue;

        _crossPoints = _crossPoints.sort((point1, point2) => {
          return lineSeg.pointToLineDistance(point1) - lineSeg.pointToLineDistance(point2);
        });
        if (lineSeg.pointToLineDistance(_crossPoints[0]) > dynamicAbsorbDistance) continue;

        intersected = true;
        this.addAbsorbPoint(_crossPoints[0], subLineInfo);
        this.addAbsorbPoint(_crossPoints[0], otherSubLineInfo);
      }

      // 如果没有交点，添加垂足
      if (!intersected) {
        this.addAbsorbPoint(subLineInfo.absorbPoint, subLineInfo);
      }
    }
  }

  private _clear() {
    this._eventDispose();
    this._pointRTree.clear();
    this.absorbPointMap.clear();
    this._subLineInfos = [];
    this._addedLineHash.clear();
    this._extraLineInfos = [];
    // 归零而不是留着上一帧的矩形：新增 calculate 入口时漏调 _updateFilterBounds 只会退化成不过滤
    this._filterBounds = null;
  }
}
